import React, { useState } from 'react';
import Icon from '../AppIcon';
import Button from './Button';
import { supabase } from '../../lib/supabase';
import { validateField, voucherValidationSchema } from '../../utils/validation';

const VoucherCodeValidator = ({ dealershipId, onValidate }) => {
  const [voucherCode, setVoucherCode] = useState('');
  const [isValidating, setIsValidating] = useState(false);
  const [validationResult, setValidationResult] = useState(null);
  const [error, setError] = useState('');

  const formatVoucherCode = (value) => {
    const cleaned = value?.replace(/[^A-Z0-9]/g, '')?.toUpperCase();
    const parts = [];
    for (let i = 0; i < cleaned?.length; i += 4) {
      parts?.push(cleaned?.substring(i, i + 4));
    }
    return parts?.join('-')?.substring(0, 14);
  };

  const handleInputChange = (e) => {
    const formatted = formatVoucherCode(e?.target?.value);
    setVoucherCode(formatted);
    setError('');
    setValidationResult(null);
    
    // Real-time validation
    if (formatted?.length === 14) {
      const validationError = validateField('voucherCode', formatted, voucherValidationSchema?.voucherCode);
      if (validationError) {
        setError(validationError);
      }
    }
  };

  const handleValidate = async () => {
    // Validate format first
    const validationError = validateField('voucherCode', voucherCode, voucherValidationSchema?.voucherCode);
    if (validationError) {
      setError(validationError);
      return;
    }

    setIsValidating(true);
    setError('');

    try {
      // Query real voucher data from Supabase with dealership currency
      const { data: voucher, error: voucherError } = await supabase
        ?.from('vouchers')
        ?.select('*, applications(*), dealerships(voucher_currency)')
        ?.eq('voucher_code', voucherCode?.trim()?.toUpperCase())
        ?.single();

      if (voucherError || !voucher) {
        setValidationResult({
          isValid: false,
          message: 'Ungültiger Gutscheincode - Code nicht gefunden',
          code: voucherCode
        });
        setIsValidating(false);
        return;
      }

      // CRITICAL SECURITY CHECK: Verify voucher belongs to this dealership
      if (dealershipId && voucher?.dealership_id !== dealershipId) {
        setValidationResult({
          isValid: false,
          securityViolation: true,
          message: 'Sicherheitsfehler: Dieser Gutschein wurde über einen anderen Partner registriert',
          details: 'Dieser Gutschein kann nur von dem Partner eingelöst werden, über den sich der Kunde registriert hat.',
          code: voucherCode
        });
        setIsValidating(false);
        return;
      }

      // Get currency from dealership, default to 'Euro'
      const currency = voucher?.dealerships?.voucher_currency || 'Euro';
      const currencySymbol = currency === 'Euro' ? '€' : currency;

      // CRITICAL: Check if application is already redeemed FIRST
      if (voucher?.applications?.application_status === 'redeemed') {
        const redemptionDate = voucher?.applications?.redeemed_at
          ? new Date(voucher?.applications?.redeemed_at)?.toLocaleDateString('de-DE', {
              year: 'numeric',
              month: 'long',
              day: 'numeric',
              hour: '2-digit',
              minute: '2-digit'
            })
          : 'Datum unbekannt';
        
        setValidationResult({
          isValid: false,
          alreadyRedeemed: true,
          message: 'Keine weitere Einlösung möglich',
          redemptionDate: redemptionDate,
          code: voucherCode,
          customerName: voucher?.applications?.customer_name,
          licensePlate: voucher?.applications?.customer_license_plate || 'Nicht angegeben',
          vehicleModel: voucher?.applications?.vehicle_model || 'Nicht angegeben',
          chassisNumber: voucher?.applications?.chassis_number,
          applicationDate: new Date(voucher?.applications?.application_date)?.toLocaleDateString('de-DE'),
          premiumAmount: voucher?.applications?.premium_amount ? `${voucher?.applications?.premium_amount?.toFixed(2)} ${currencySymbol}` : 'Nicht festgelegt',
          currency: currency
        });
        setIsValidating(false);
        return;
      }

      // Check voucher status
      let statusMessage = '';
      let isValid = true;

      switch (voucher?.voucher_status) {
        case 'used':
          const redemptionDate = voucher?.verification_date 
            ? new Date(voucher?.verification_date)?.toLocaleDateString('de-DE', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
              })
            : 'Datum unbekannt';
          statusMessage = `Dieser Gutschein wurde bereits eingelöst am ${redemptionDate}`;
          isValid = false;
          break;
        case 'expired':
          statusMessage = 'Dieser Gutschein ist abgelaufen';
          isValid = false;
          break;
        case 'cancelled':
          statusMessage = 'Dieser Gutschein wurde storniert';
          isValid = false;
          break;
        case 'active':
          statusMessage = 'Gutschein erfolgreich verifiziert und gültig';
          isValid = true;
          break;
        default:
          statusMessage = 'Gutschein-Status unbekannt';
          isValid = false;
      }

      // Create verification record if valid
      if (isValid && dealershipId) {
        const { error: verificationError } = await supabase
          ?.from('voucher_verifications')
          ?.insert([
            {
              voucher_id: voucher?.id,
              dealership_id: dealershipId,
              verification_date: new Date()?.toISOString(),
              verification_method: 'web_portal',
              verified_by: 'Dealership Staff'
            }
          ]);

        if (verificationError) {
          console.error('Failed to create verification record:', verificationError);
        }

        // Update voucher status to 'used' and verification count
        const newCount = (voucher?.verification_count || 0) + 1;
        await supabase
          ?.from('vouchers')
          ?.update({ 
            voucher_status: 'used',
            verification_count: newCount,
            verification_date: new Date()?.toISOString()
          })
          ?.eq('id', voucher?.id);

        // Update application status to 'redeemed' with timestamp
        if (voucher?.application_id) {
          const redemptionTimestamp = new Date()?.toISOString();
          const { error: applicationUpdateError } = await supabase
            ?.from('applications')
            ?.update({ 
              application_status: 'redeemed',
              redeemed_at: redemptionTimestamp,
              updated_at: redemptionTimestamp
            })
            ?.eq('id', voucher?.application_id);

          if (applicationUpdateError) {
            console.error('Failed to update application status:', applicationUpdateError);
          }
        }
      }

      const result = {
        isValid,
        message: statusMessage,
        code: voucherCode,
        customerName: voucher?.applications?.customer_name,
        licensePlate: voucher?.applications?.customer_license_plate || 'Nicht angegeben',
        vehicleModel: voucher?.applications?.vehicle_model || 'Nicht angegeben',
        chassisNumber: voucher?.applications?.chassis_number,
        applicationDate: new Date(voucher?.applications?.application_date)?.toLocaleDateString('de-DE'),
        premiumAmount: voucher?.applications?.premium_amount ? `${voucher?.applications?.premium_amount?.toFixed(2)} ${currencySymbol}` : 'Nicht festgelegt',
        status: voucher?.voucher_status,
        validatedAt: new Date()?.toISOString(),
        currency: currency
      };

      setValidationResult(result);

      if (onValidate) {
        onValidate(result);
      }

    } catch (err) {
      console.error('Verification error:', err);
      setError('Fehler bei der Verifizierung. Bitte versuchen Sie es erneut.');
    } finally {
      setIsValidating(false);
    }
  };

  const handleReset = () => {
    setVoucherCode('');
    setValidationResult(null);
    setError('');
  };

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="w-full max-w-2xl mx-auto">
      <div className="bg-card rounded-lg shadow-card p-8 space-y-6">
        <div className="text-center space-y-2">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-primary/10 rounded-full mb-4">
            <Icon name="CheckCircle2" size={32} color="var(--color-primary)" />
          </div>
          <h2 className="text-2xl font-heading font-semibold text-foreground">
            Gutschein verifizieren
          </h2>
          <p className="text-sm text-muted-foreground">
            Geben Sie den Gutscheincode des Kunden ein, um die THG-Prämie zu validieren
          </p>
        </div>

        {!validationResult ? (
          <div className="space-y-4">
            <div className="space-y-2">
              <label className="block text-sm font-medium text-foreground">
                Gutscheincode
              </label>
              <input
                type="text"
                value={voucherCode}
                onChange={handleInputChange}
                placeholder="XXXX-XXXX-XXXX"
                className={`w-full px-4 py-4 border rounded-lg bg-background text-foreground font-mono text-xl text-center focus:outline-none focus:ring-2 focus:ring-ring transition-smooth tracking-wider ${
                  error ? 'border-error' : 'border-input'
                }`}
                maxLength={14}
                autoComplete="off"
                aria-invalid={!!error}
              />
              <p className="text-xs text-muted-foreground text-center">
                Format: 4 Zeichen - 4 Zeichen - 4 Zeichen
              </p>
            </div>

            {error && (
              <div className="flex items-center gap-2 p-4 bg-error/10 border border-error/20 rounded-lg">
                <Icon name="AlertCircle" size={20} color="var(--color-error)" />
                <p className="text-sm text-error">{error}</p>
              </div>
            )}

            <Button
              variant="default"
              fullWidth
              size="lg"
              onClick={handleValidate}
              disabled={voucherCode?.length !== 14 || isValidating || !!error}
              loading={isValidating}
              iconName="CheckCircle2"
              iconPosition="left"
            >
              Code validieren
            </Button>
          </div>
        ) : (
          <div className="space-y-6">
            <div className={`p-6 rounded-lg border-2 ${
              validationResult?.isValid
                ? 'bg-success/10 border-success/20' :'bg-error/10 border-error/20'
            }`}>
              <div className="flex items-center gap-3 mb-4">
                <Icon
                  name={validationResult?.isValid ? 'CheckCircle2' : 'XCircle'}
                  size={32}
                  color={validationResult?.isValid ? 'var(--color-success)' : 'var(--color-error)'}
                />
                <div>
                  <h3 className={`text-xl font-heading font-semibold ${
                    validationResult?.isValid ? 'text-success' : 'text-error'
                  }`}>
                    {validationResult?.message}
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    Code: {validationResult?.code}
                  </p>
                  {validationResult?.alreadyRedeemed && (
                    <p className="text-sm font-medium text-error mt-2">
                      Eingelöst am: {validationResult?.redemptionDate}
                    </p>
                  )}
                  {validationResult?.securityViolation && (
                    <p className="text-sm font-medium text-error mt-2">
                      {validationResult?.details}
                    </p>
                  )}
                </div>
              </div>

              {validationResult?.securityViolation && (
                <div className="space-y-3 pt-4 border-t border-border">
                  <div className="bg-error/5 rounded-lg p-4">
                    <div className="flex items-start gap-2">
                      <Icon name="Shield" size={20} color="var(--color-error)" className="flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-semibold text-error mb-1">
                          Partner-spezifische Einlösung erforderlich
                        </p>
                        <p className="text-xs text-muted-foreground">
                          Dieser Gutschein ist an einen anderen Partner gebunden. Gutscheine können nur von dem Autohaus eingelöst werden, über das sich der Kunde ursprünglich registriert hat. Dies dient dem Schutz der Partner-Provisionen und verhindert unbefugte Einlösungen.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {validationResult?.alreadyRedeemed && (
                <div className="space-y-3 pt-4 border-t border-border">
                  <div className="bg-error/5 rounded-lg p-4 mb-4">
                    <div className="flex items-start gap-2">
                      <Icon name="AlertCircle" size={20} color="var(--color-error)" className="flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-semibold text-error mb-1">
                          Keine weitere Einlösung möglich
                        </p>
                        <p className="text-xs text-muted-foreground">
                          Dieser Gutschein wurde bereits am {validationResult?.redemptionDate} eingelöst und kann nicht erneut verwendet werden.
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Kunde:</span>
                    <span className="text-sm font-medium text-foreground">{validationResult?.customerName}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Kennzeichen:</span>
                    <span className="text-sm font-medium text-foreground">{validationResult?.licensePlate}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Fahrzeug:</span>
                    <span className="text-sm font-medium text-foreground">{validationResult?.vehicleModel}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">FIN:</span>
                    <span className="text-sm font-mono text-foreground">{validationResult?.chassisNumber}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Antragsdatum:</span>
                    <span className="text-sm font-medium text-foreground">{validationResult?.applicationDate}</span>
                  </div>
                  <div className="flex justify-between items-center pt-3 border-t border-border">
                    <span className="text-sm font-medium text-foreground">Prämienbetrag:</span>
                    <span className="text-lg font-heading font-semibold text-muted-foreground line-through">{validationResult?.premiumAmount}</span>
                  </div>
                </div>
              )}

              {validationResult?.isValid && (
                <div className="space-y-3 pt-4 border-t border-border">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Kunde:</span>
                    <span className="text-sm font-medium text-foreground">{validationResult?.customerName}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Kennzeichen:</span>
                    <span className="text-sm font-medium text-foreground">{validationResult?.licensePlate}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Fahrzeug:</span>
                    <span className="text-sm font-medium text-foreground">{validationResult?.vehicleModel}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">FIN:</span>
                    <span className="text-sm font-mono text-foreground">{validationResult?.chassisNumber}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Antragsdatum:</span>
                    <span className="text-sm font-medium text-foreground">{validationResult?.applicationDate}</span>
                  </div>
                  <div className="flex justify-between items-center pt-3 border-t border-border">
                    <span className="text-sm font-medium text-foreground">Prämienbetrag:</span>
                    <span className="text-lg font-heading font-semibold text-success">{validationResult?.premiumAmount}</span>
                  </div>
                </div>
              )}
            </div>

            <div className="flex gap-3">
              <Button
                variant="outline"
                fullWidth
                onClick={handleReset}
                iconName="RotateCcw"
                iconPosition="left"
              >
                Neuer Code
              </Button>
              {validationResult?.isValid && (
                <Button
                  variant="default"
                  fullWidth
                  onClick={handlePrint}
                  iconName="Printer"
                  iconPosition="left"
                >
                  Seite drucken
                </Button>
              )}
            </div>
          </div>
        )}
      </div>
      <div className="mt-6 p-4 bg-muted/50 rounded-lg">
        <div className="flex items-start gap-3">
          <Icon name="Info" size={20} color="var(--color-primary)" className="flex-shrink-0 mt-0.5" />
          <div className="space-y-1">
            <p className="text-sm font-medium text-foreground">
              Hinweise zur Gutscheinvalidierung
            </p>
            <ul className="text-xs text-muted-foreground space-y-1 list-disc list-inside">
              <li>Gutscheincodes sind einmalig verwendbar</li>
              <li>Die Validierung erfolgt in Echtzeit gegen die Grid Balance Datenbank</li>
              <li>Bei Problemen kontaktieren Sie bitte den Support</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VoucherCodeValidator;